import { useRef, useState } from "react";
import { ImageOff } from "lucide-react";

/**
 * Image helper for admin-authored content.
 *
 * Staff photograph containers on phones, so uploads arrive as an unpredictable
 * mix of 4:3, 16:9 and portrait. Neither fit mode alone handles that:
 * `object-cover` fills the card but would slice the top and bottom off a
 * portrait shot, while `object-contain` never crops but pads every landscape
 * photo with bars it does not need.
 *
 * So the mode is chosen per image, once it has loaded. We compute how much of
 * the frame `cover` would actually discard; below the threshold the crop is
 * imperceptible and the card fills edge-to-edge, above it the whole photo is
 * shown on a neutral mat instead of being butchered. In practice landscape and
 * square uploads fill, and only portrait gets padding.
 */

/** Hosts whose CDN understands resize/format query params. */
const UNSPLASH = "images.unsplash.com";
const PEXELS = "images.pexels.com";

/**
 * Share of the image `cover` may discard before we stop cropping.
 *
 * Against the 4:3 card box this puts 16:9 and 1:1 uploads at ~25% loss — the
 * subject is centred, so that crop is safe and they fill the card. Portrait
 * lands at ~44% (3:4) and ~58% (9:16), which would cut a container in half, so
 * those switch to the padded mode.
 */
const MAX_CROP_LOSS = 0.3;

/**
 * Ask the source CDN for an appropriately sized file.
 *
 * `fit=max` rather than `fit=crop`: fitting is decided here at render time, so
 * cropping server-side as well would discard pixels before we can judge them.
 * Unknown hosts (Firebase Storage, and anything an admin pastes) are returned
 * untouched — appending params they ignore just makes the URL noisy, and
 * Storage download URLs are already signed.
 */
export function optimizedSrc(src, width) {
    if (!src || typeof src !== "string") return "";
    if (!width) return src;

    try {
        const url = new URL(src, window.location.origin);
        if (url.hostname === UNSPLASH) {
            url.searchParams.set("auto", "format");
            url.searchParams.set("fit", "max");
            url.searchParams.set("w", String(width));
            url.searchParams.set("q", "80");
            return url.toString();
        }
        if (url.hostname === PEXELS) {
            url.searchParams.set("auto", "compress");
            url.searchParams.set("cs", "tinysrgb");
            url.searchParams.set("w", String(width));
            return url.toString();
        }
        return src;
    } catch {
        // Not a parseable URL (relative path, data URI) — use it as-is.
        return src;
    }
}

function Placeholder() {
    // No image, or the URL 404'd. Content is admin-authored, so an item without
    // artwork is a normal state rather than an error worth shouting about.
    return (
        <div
            className="flex h-full w-full flex-col items-center justify-center gap-2 bg-cs-navy/5 py-10 text-cs-steel/50"
            data-testid="smart-image-placeholder"
        >
            <ImageOff size={22} strokeWidth={1.5} />
            <span className="font-mono text-[9px] uppercase tracking-widest">No image</span>
        </div>
    );
}

export default function SmartImage({
    src,
    alt = "",
    width = 1000,
    className = "",
    imgClassName = "",
    loading = "lazy",
    /** Render at the image's own aspect ratio instead of filling a fixed box. */
    natural = false,
    /** Crop anchor while filling. "top" keeps infographic headings visible. */
    position = "top",
    /** Mat colour behind a padded (portrait) image. */
    padClassName = "bg-cs-offwhite",
}) {
    const [failed, setFailed] = useState(false);
    // Start on cover: the common case, and it avoids a visible mode flip for
    // every landscape upload.
    const [fit, setFit] = useState("cover");
    const boxRef = useRef(null);

    const usable = Boolean(src) && !failed;

    function handleLoad(event) {
        const img = event.currentTarget;
        const box = boxRef.current;
        if (!img.naturalWidth || !img.naturalHeight || !box) return;

        const rect = box.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const imageRatio = img.naturalWidth / img.naturalHeight;
        const boxRatio = rect.width / rect.height;
        // Symmetric: works whether the image is wider or taller than the box.
        const loss = 1 - Math.min(imageRatio, boxRatio) / Math.max(imageRatio, boxRatio);

        setFit(loss > MAX_CROP_LOSS ? "contain" : "cover");
    }

    if (natural) {
        return (
            <div className={`overflow-hidden ${className}`}>
                {usable ? (
                    <img
                        src={optimizedSrc(src, width)}
                        alt={alt}
                        loading={loading}
                        onError={() => setFailed(true)}
                        className={`block h-auto w-full ${imgClassName}`}
                    />
                ) : (
                    <Placeholder />
                )}
            </div>
        );
    }

    const padded = fit === "contain";

    return (
        <div
            ref={boxRef}
            className={`relative overflow-hidden ${padded ? padClassName : "bg-cs-navy/5"} ${className}`}
        >
            {usable ? (
                <img
                    src={optimizedSrc(src, width)}
                    alt={alt}
                    loading={loading}
                    onLoad={handleLoad}
                    onError={() => setFailed(true)}
                    // Anchoring only means anything while cropping; a padded
                    // image is centred in its mat.
                    style={padded ? undefined : { objectPosition: position === "top" ? "50% 0%" : position }}
                    className={`h-full w-full ${padded ? "object-contain" : "object-cover"} ${imgClassName}`}
                />
            ) : (
                <Placeholder />
            )}
        </div>
    );
}
